import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { qaqcApi, materialsApi, projectsApi, usersApi, type PourCard, type QaInspection } from '@/lib/apiClient';
import { StateView } from '@/components';
import { useFocusTrap } from '@/lib/hooks/useFocusTrap';
import { POUR_TYPE_LABEL, pourStatus, pourStatusLabel, qtyDisplay, isRock, fmtDt, fmtWallD, PourAction, MixFlag } from '../qaqc/pourShared';
import PourCardDrawer from '../qaqc/PourCardDrawer';
import MixDesignsCard from '../qaqc/MixDesignsCard';
import ConcreteSetupCard from '../qaqc/ConcreteSetupCard';
import AddPourCardForm from '../qaqc/AddPourCardForm';
import PourLogTable from '../qaqc/PourLogTable';
import RockLogTable from '../qaqc/RockLogTable';

// QA/QC (CMS expansion): read-only mirror of the two Formstack forms —
// Pre-Pour Checklists (the field's concrete order, "Pour Cards") and Quality
// Job Site Inspection Reports. The tracker-side writes are the office actions
// (projects.edit): Confirm order, Mark poured, and "+ Add order" for an order
// that never came through the form (phoned in, or the app was down). Data
// otherwise arrives via HMAC webhooks; do not rebuild the forms here.

// Status/type maps, vendor email, and the office action buttons live in
// ../qaqc/pourShared — shared with the company-wide QA/QC roll-up page.

const css = `
  .qq-table { width: 100%; border-collapse: collapse; font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); }
  .qq-table th {
    text-align: left; padding: 10px var(--ew-s-4); white-space: nowrap;
    font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-500); border-bottom: 1px solid var(--ew-n-100);
  }
  .qq-table td { padding: 11px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100); color: var(--ew-dark); vertical-align: top; }
  .qq-table tr:last-child td { border-bottom: 0; }
  .qq-table tbody tr.qq-click { cursor: pointer; }
  .qq-table tbody tr.qq-click:hover td { background: var(--ew-n-50, #FAFAFA); }
  .qq-muted { color: var(--ew-n-500); }
  .qq-chip {
    display: inline-block; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-pill);
    padding: 2px 9px; font: var(--ew-fw-semibold) 11px/1.5 var(--ew-font); color: var(--ew-n-600); white-space: nowrap;
  }
  .qq-badge {
    display: inline-flex; align-items: center; gap: 6px; border-radius: var(--ew-r-pill);
    padding: 3px 10px; font: var(--ew-fw-semibold) 11px/1.4 var(--ew-font); white-space: nowrap;
  }
`;

export default function QaqcTab({ projectId }: { projectId: number }) {
  const pourQuery = useQuery({ queryKey: ['qaqc-pours', projectId], queryFn: () => qaqcApi.listPourCards(projectId) });
  const inspQuery = useQuery({ queryKey: ['qaqc-inspections', projectId], queryFn: () => qaqcApi.listInspections(projectId) });

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60 * 1000 });
  const canEdit = (me?.roles.includes('Admin') ?? false) || (me?.permissions.includes('projects.edit') ?? false);

  // Materials lines for the commitment picker + job identity for the email.
  const { data: materials } = useQuery({
    queryKey: ['materials', projectId],
    queryFn: () => materialsApi.list({ projectId }),
    staleTime: 60_000,
  });
  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId) as Promise<{ jobNumber: string }>,
    staleTime: 60_000,
  });
  const jobNumber = projectQuery.data?.jobNumber ?? '';

  // Supersede hint: an open Will-Call is probably stale once a NEWER Main
  // Pour card exists for the job (the field firms up by submitting a new
  // Pre-Pour form — the established process). The office still clicks Cancel.
  const newestMainAt = useMemo(() => {
    const mains = (pourQuery.data ?? []).filter((c) => c.pourType === 'main');
    return mains.length ? Math.max(...mains.map((c) => new Date(c.createdAt).getTime())) : null;
  }, [pourQuery.data]);
  const isSuperseded = (c: PourCard) =>
    c.pourType === 'willcall' && (c.status === 'pending' || c.status === 'confirmed')
    && newestMainAt !== null && new Date(c.createdAt).getTime() < newestMainAt;

  const [openCard, setOpenCard] = useState<number | null>(null);
  const [addingOrder, setAddingOrder] = useState(false);
  // Orders ↔ Pour log (filter / sort / total / export, for reconciling yards)
  // ↔ Rock log (the same for Rock/Aggregate deliveries, totaled per unit).
  const [pourView, setPourView] = useState<'orders' | 'log' | 'rock'>('orders');
  const [openInspection, setOpenInspection] = useState<number | null>(null);

  const openedCard = useMemo(
    () => (pourQuery.data ?? []).find((c) => c.id === openCard) ?? null,
    [pourQuery.data, openCard],
  );
  const openedInspection = useMemo(
    () => (inspQuery.data ?? []).find((i) => i.id === openInspection) ?? null,
    [inspQuery.data, openInspection],
  );

  return (
    <>
      <style>{css}</style>

      {/* ── Formstack banner ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ew-s-3)', background: '#fff', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '10px var(--ew-s-4)', marginBottom: 'var(--ew-s-4)', flexWrap: 'wrap' }}>
        <span style={{ font: 'var(--ew-fw-semibold) 11px/1 var(--ew-font)', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ew-quality)' }}>◇ Synced from Formstack</span>
        <span style={{ font: 'var(--ew-fw-regular) 12px var(--ew-font)', color: 'var(--ew-n-500)' }}>
          QA/QC inspections, Pre-Pour Checklists and Rock/Aggregate orders flow in via webhook · orders placed without a form can be added below
        </span>
      </div>

      {/* ── Pour Cards ── */}
      <div className="pw-card" style={{ marginBottom: 'var(--ew-s-4)' }}>
        <h2 className="pw-card-hd" style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
          <span>Pour Cards · Concrete &amp; Rock Orders</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 12, textTransform: 'none', letterSpacing: 0 }}>
            {canEdit && !addingOrder && (
              <button className="ew-btn-link" style={{ color: 'var(--ew-green)', font: 'var(--ew-fw-medium) 12px var(--ew-font)' }}
                      title="Add an order that didn't come through the Pre-Pour Checklist"
                      onClick={() => setAddingOrder(true)}>
                + Add order
              </button>
            )}
            <span style={{ display: 'flex', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-pill)', overflow: 'hidden' }}>
              {(['orders', 'log', 'rock'] as const).map((v) => (
                <button key={v} type="button" onClick={() => setPourView(v)} aria-pressed={pourView === v} style={{
                  border: 0, cursor: 'pointer', padding: '5px 12px', font: 'var(--ew-fw-medium) 12px/1 var(--ew-font)',
                  background: pourView === v ? 'var(--ew-dark)' : '#fff', color: pourView === v ? '#fff' : 'var(--ew-n-600)',
                }}>{v === 'orders' ? 'Orders' : v === 'log' ? 'Pour log' : 'Rock log'}</button>
              ))}
            </span>
          </span>
        </h2>
        <div style={{ fontSize: 12, color: 'var(--ew-n-500)', padding: '10px var(--ew-s-4) 0' }}>
          Pre-Pour Checklists double as the field's concrete order, and the Rock/Aggregate form as its rock order — the office confirms each order with the vendor &amp; field leader.
        </div>
        {addingOrder && <AddPourCardForm projectId={projectId} onDone={() => setAddingOrder(false)} onSaved={(card) => setOpenCard(card.id)} />}
        <StateView query={pourQuery} empty="No pour cards for this job yet — they arrive when the field submits a Pre-Pour Checklist or a Rock/Aggregate order, or use + Add order.">
          {(cards) => pourView === 'rock' ? (
            <div style={{ marginTop: 10 }}>
              <RockLogTable cards={cards} showJob={false} fileName={jobNumber ? `rock-log-${jobNumber}` : 'rock-log'} onOpen={setOpenCard} />
            </div>
          ) : pourView === 'log' ? (
            <div style={{ marginTop: 10 }}>
              <PourLogTable cards={cards} showJob={false} fileName={jobNumber ? `pour-log-${jobNumber}` : 'pour-log'} onOpen={setOpenCard} />
            </div>
          ) : (
            <div className="ew-table-scroll">
              <table className="qq-table ew-cards">
                <thead>
                  <tr>
                    <th>Element</th>
                    <th>Pour Date</th>
                    <th>Mix ID</th>
                    <th>Qty</th>
                    <th>Vendor</th>
                    <th>Pour Type</th>
                    <th>Order</th>
                    {canEdit ? <th aria-label="Actions" /> : null}
                  </tr>
                </thead>
                <tbody>
                  {cards.map((c) => {
                    const st = pourStatus(c.status);
                    return (
                      <tr key={c.id} className="qq-click" onClick={() => setOpenCard(c.id)}>
                        <td data-label="Element">
                          <div style={{ fontWeight: 500 }}>
                            {c.element ?? '—'}
                            {c.source === 'manual' && (
                              <span className="qq-chip" style={{ marginLeft: 8 }}
                                    title={`Entered by the office${c.createdBy ? ` (${c.createdBy})` : ''} — no ${isRock(c) ? 'Rock/Aggregate form' : 'Pre-Pour Checklist'} behind it`}>
                                Office entry
                              </span>
                            )}
                            {isSuperseded(c) && (
                              <span style={{ marginLeft: 8, font: 'var(--ew-fw-semibold) 10px/1.5 var(--ew-font)', textTransform: 'uppercase', letterSpacing: '.04em', background: 'var(--ew-safety-50)', color: 'var(--ew-safety-700)', border: '1px solid var(--ew-safety-100)', borderRadius: 'var(--ew-r-pill)', padding: '2px 8px' }}
                                title="A newer Main Pour card arrived after this will-call — it's likely been firmed up. Cancel this one if so.">
                                New main pour received
                              </span>
                            )}
                          </div>
                          {c.location && <div className="qq-muted" style={{ fontSize: 12, marginTop: 2 }}>{c.location}</div>}
                        </td>
                        <td data-label="Pour Date" style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmtDt(c.pourDatetime)}</td>
                        <td className="qq-muted" data-label="Mix ID">{c.mix ?? '—'}<MixFlag card={c} size="sm" /></td>
                        <td className="qq-muted" data-label="Qty">{qtyDisplay(c) ?? '—'}</td>
                        <td className="qq-muted" data-label="Vendor">{c.vendor ?? '—'}</td>
                        <td data-label="Pour Type"><span className="qq-chip">{POUR_TYPE_LABEL[c.pourType] ?? c.pourType}</span></td>
                        <td data-label="Order">
                          <span className="qq-badge" style={{ background: st.bg, color: st.color }}>
                            <span style={{ width: 6, height: 6, borderRadius: 999, background: st.dot }} />
                            {pourStatusLabel(c.status, c.pourType)}
                          </span>
                        </td>
                        {canEdit ? (
                          <td onClick={(e) => e.stopPropagation()}>
                            <PourAction card={c} jobNumber={jobNumber} size="sm" />
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </StateView>
      </div>

      {/* ── The job's concrete supplier & blanket POs (estimator raises the PO, supplier varies by job; read on every
             pour card and beside the job on the pour schedule) ── */}
      <ConcreteSetupCard projectId={projectId} canEdit={canEdit} />

      {/* ── Approved mix designs (PM-owned register the pours are checked against) ── */}
      <MixDesignsCard projectId={projectId} canEdit={canEdit} cards={pourQuery.data ?? []} />

      {/* ── Inspections ── */}
      <div className="pw-card">
        <h2 className="pw-card-hd">QA/QC Inspections</h2>
        <StateView query={inspQuery} empty="No inspections for this job yet — they arrive when a Quality Job Site Inspection Report is submitted.">
          {(inspections) => (
            <div className="ew-table-scroll">
              <table className="qq-table ew-cards">
                <thead>
                  <tr>
                    <th>Inspection</th>
                    <th>Inspector</th>
                    <th>Date</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {inspections.map((i) => (
                    <tr key={i.id} className="qq-click" onClick={() => setOpenInspection(i.id)}>
                      <td data-label="Inspection">
                        <div style={{ fontWeight: 500 }}>Job Site Inspection</div>
                        {i.jobText && <div className="qq-muted" style={{ fontSize: 12, marginTop: 2 }}>{i.jobText}</div>}
                      </td>
                      <td className="qq-muted" data-label="Inspector">{i.inspectorName ?? '—'}{i.inspectorTitle ? ` · ${i.inspectorTitle}` : ''}</td>
                      <td data-label="Date" style={{ whiteSpace: 'nowrap' }}>{fmtWallD(i.inspectDate)}</td>
                      <td data-label="Result">
                        {i.noCount > 0 ? (
                          <span className="qq-badge" style={{ background: 'var(--ew-danger-50)', color: 'var(--ew-danger-700)' }}>
                            <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--ew-danger)' }} />
                            {i.noCount} item{i.noCount === 1 ? '' : 's'} flagged
                          </span>
                        ) : (
                          <span className="qq-badge" style={{ background: 'var(--ew-green-50)', color: 'var(--ew-green-700)' }}>
                            <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--ew-green)' }} />
                            Pass
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </StateView>
        <div style={{ padding: '10px var(--ew-s-4)', borderTop: '1px solid var(--ew-n-100)', font: 'var(--ew-fw-regular) 12px var(--ew-font)', color: 'var(--ew-n-500)' }}>
          Result is derived from the report's answers — any “No” flags the inspection; open a row for the full item list.
        </div>
      </div>

      {openedCard && (
        <PourCardDrawer card={openedCard} jobNumber={jobNumber} canEdit={canEdit}
          materials={materials ?? []} onClose={() => setOpenCard(null)} />
      )}
      {openedInspection && (
        <InspectionDrawer inspection={openedInspection} onClose={() => setOpenInspection(null)} />
      )}
    </>
  );
}

// PourCardDrawer moved to ../qaqc/PourCardDrawer (shared with the roll-up page).

// ─── Inspection drawer (full item list by section) ───────────────────────────
function InspectionDrawer({ inspection, onClose }: { inspection: QaInspection; onClose: () => void }) {
  const drawerRef = useRef<HTMLDivElement>(null);
  useFocusTrap(drawerRef, { onEscape: onClose });

  const bySection = inspection.items.reduce<Record<string, typeof inspection.items>>((acc, it) => {
    (acc[it.section || 'General'] ??= []).push(it);
    return acc;
  }, {});

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(54,54,54,0.32)', zIndex: 200 }} />
      <div ref={drawerRef} style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 500, maxWidth: '100vw', background: '#fff', zIndex: 201, overflowY: 'auto', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--ew-n-100)', position: 'sticky', top: 0, background: '#fff', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ font: 'var(--ew-fw-semibold) 10px/1 var(--ew-font)', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ew-n-500)' }}>Quality Job Site Inspection</div>
            <div style={{ font: 'var(--ew-fw-semibold) 17px/1.3 var(--ew-font)', margin: '6px 0 2px' }}>{inspection.jobText ?? 'Inspection'}</div>
            <div style={{ fontSize: 12, color: 'var(--ew-n-500)' }}>
              {inspection.inspectorName ?? '—'}{inspection.inspectorTitle ? ` · ${inspection.inspectorTitle}` : ''} · {fmtWallD(inspection.inspectDate)}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close drawer" style={{ fontSize: 18, color: 'var(--ew-n-500)', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ padding: '4px 20px 32px' }}>
          {inspection.summary && (
            <>
              <div style={{ font: 'var(--ew-fw-semibold) 11px/1 var(--ew-font)', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ew-green)', margin: '18px 0 8px' }}>Summary</div>
              <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', color: 'var(--ew-dark)' }}>{inspection.summary}</div>
            </>
          )}
          <div style={{ font: 'var(--ew-fw-semibold) 11px/1 var(--ew-font)', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ew-green)', margin: '22px 0 8px' }}>Inspection Items</div>
          {Object.entries(bySection).map(([section, items]) => (
            <div key={section} style={{ marginBottom: 12 }}>
              {section !== 'General' && <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ew-n-500)', textTransform: 'uppercase', letterSpacing: '.05em', margin: '8px 0 4px' }}>{section}</div>}
              {items.map((it, idx) => (
                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', gap: 14, fontSize: 12, padding: '5px 0', borderBottom: '1px solid var(--ew-n-100)' }}>
                  <span style={{ color: 'var(--ew-n-600)' }}>{it.label}</span>
                  <span style={{ fontWeight: 600, whiteSpace: 'nowrap', color: it.value.toLowerCase() === 'no' ? 'var(--ew-danger)' : 'var(--ew-dark)' }}>{it.value}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
